import { getDataWithParams, postDataWithParams } from 'src/consumer/services/profileService'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'

// Game options for the engagement selector
export const ENGAGEMENT_GAME_OPTIONS = [
  { value: 'music', label: 'Music Player', description: 'Soothing background music' },
  { value: 'breathing', label: 'Breathing Exercise', description: 'Guided breathing activity' },
  { value: 'memory', label: 'Memory Game', description: 'Card matching puzzle' },
  { value: 'bubbles', label: 'Bubble Pop', description: 'Relaxing bubble popping' },
]

// API endpoints
const PATIENT_ENGAGEMENT_BASE = '/api/secure/cc/support/patient/engagement'
const BOOKED_EVENT_ENGAGEMENT_BASE = '/api/secure/cc/support/booked-event/engagement'
const BG_MUSIC_ENDPOINT = '/api/secure/cc/support/engagement/bg-music'

/**
 * Check if current user can manage engagement settings
 * Requires L2/L3 Therapy Support roles
 */
export function canManageEngagement() {
  const allRoles = getAllRolesInBoolean()
  return (
    allRoles.isL2TherapySupport ||
    allRoles.isL3TherapySupport ||
    allRoles.isClinicSuccessManager ||
    allRoles.isClinicSuccessAdmin ||
    allRoles.isAdmin
  )
}

/**
 * Fetch available background music tracks
 */
export async function getBackgroundMusicTracks(enabled = true) {
  try {
    const response = await getDataWithParams(BG_MUSIC_ENDPOINT, { enabled })
    
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      const tracks = response?.data?.data?.backgroundMusic || []
      // Map to the format expected by the engagement config
      return {
        success: true,
        tracks: tracks.map(track => ({
          id: track.id,
          name: track.name,
          mediaId: track.fileId,
          enabled: track.enabled,
        })),
      }
    }
    
    return { success: false, tracks: [], message: response?.data?.message || 'Failed to fetch music tracks' }
  } catch (error) {
    console.error('Error fetching background music:', error)
    return { success: false, tracks: [], message: 'Network error' }
  }
}

/**
 * Get patient engagement config
 */
export async function getPatientEngagement(patientId) {
  try {
    const response = await getDataWithParams(`${PATIENT_ENGAGEMENT_BASE}/get`, { patientId })
    
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      return {
        success: true,
        config: response?.data?.data?.engagementConfig || null,
      }
    }
    
    return { success: false, config: null, message: response?.data?.message || 'Failed to fetch engagement config' }
  } catch (error) {
    console.error('Error fetching patient engagement:', error)
    return { success: false, config: null, message: 'Network error' }
  }
}

/**
 * Save patient engagement config
 */
export async function savePatientEngagement(patientId, { enabled, engagement }) {
  try {
    const response = await postDataWithParams(
      `${PATIENT_ENGAGEMENT_BASE}/save`,
      { enabled, engagement },
      { patientId }
    )
    
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      return { success: true, message: 'Engagement settings saved' }
    }
    
    return { success: false, message: response?.data?.message || 'Failed to save engagement config' }
  } catch (error) {
    console.error('Error saving patient engagement:', error)
    return { success: false, message: 'Network error' }
  }
}

/**
 * Reset (delete) patient engagement config
 */
export async function resetPatientEngagement(patientId) {
  try {
    const response = await postDataWithParams(
      `${PATIENT_ENGAGEMENT_BASE}/reset`,
      {},
      { patientId }
    )
    
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      return { success: true, message: 'Engagement settings reset' }
    }
    
    return { success: false, message: response?.data?.message || 'Failed to reset engagement config' }
  } catch (error) {
    console.error('Error resetting patient engagement:', error)
    return { success: false, message: 'Network error' }
  }
}

/**
 * Get booked event engagement config
 */
export async function getBookedEventEngagement(bookedEventId) {
  try {
    const response = await getDataWithParams(`${BOOKED_EVENT_ENGAGEMENT_BASE}/get`, { bookedEventId })
    
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      return {
        success: true,
        config: response?.data?.data?.engagementConfig || null,
      }
    }
    
    return { success: false, config: null, message: response?.data?.message || 'Failed to fetch engagement config' }
  } catch (error) {
    console.error('Error fetching booked event engagement:', error)
    return { success: false, config: null, message: 'Network error' }
  }
}

/**
 * Save booked event engagement config
 */
export async function saveBookedEventEngagement(bookedEventId, { enabled, engagement }) {
  try {
    const response = await postDataWithParams(
      `${BOOKED_EVENT_ENGAGEMENT_BASE}/save`,
      { enabled, engagement },
      { bookedEventId }
    )
    
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      return { success: true, message: 'Engagement settings saved' }
    }
    
    return { success: false, message: response?.data?.message || 'Failed to save engagement config' }
  } catch (error) {
    console.error('Error saving booked event engagement:', error)
    return { success: false, message: 'Network error' }
  }
}

/**
 * Reset (delete) booked event engagement config
 */
export async function resetBookedEventEngagement(bookedEventId) {
  try {
    const response = await postDataWithParams(
      `${BOOKED_EVENT_ENGAGEMENT_BASE}/reset`,
      {},
      { bookedEventId }
    )
    
    if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
      return { success: true, message: 'Engagement settings reset' }
    }
    
    return { success: false, message: response?.data?.message || 'Failed to reset engagement config' }
  } catch (error) {
    console.error('Error resetting booked event engagement:', error)
    return { success: false, message: 'Network error' }
  }
}
